/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { createKontrolTicket, createTicket, CreateTicketPayload } from "../request/request";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import { productData, TABS } from "settings/constants/constants";
import {
  isGeneralInfoValidCR,
  isAdditionalInfoValid as isAdditionalInfoValidShared,
  isAttachmentsValid as isAttachmentsValidShared,
  mapCRPriority,
  type PriorityLabel,
  extractUniqueCustomers,
  filterServicesByCustomer,
  isValidEmail,
  findProductByLabel,
  findTopicByLabel,
  findTwoLevelByLabel,
} from "../utils/tickets";
import { useLoadServiceItems } from "../hooks/useLoadServiceItems";
import { useWizardTabs } from "../hooks/useWizardTabs";
import { useTopicsLoader } from "../hooks/useTopicsLoader";
import { useChangeRequestForm } from "../hooks/useChangeRequestForm";
import { RequestInfoTab } from "./ChangeRequestModal/RequestInfoTab";

import { ServiceSelectionTab } from "./ChangeRequestModal/ServiceSelectionTab";
import { AttachmentsTab } from "./ChangeRequestModal/AttachmentsTabs";

interface ChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated?: () => void;
}

export const ChangeRequestModal: React.FC<ChangeRequestModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
}) => {
  const api = useAxiosAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isPartnerView = tenantId === partnerId;
  const [cloud] = useState("CloudGlobal");
  const [region] = useState("us-east-1");
  const [isSaving, setIsSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertMessage, setAlertMessage] = useState("");

  // Check if the selected product is "Kontrol Edge"
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";

  // Use custom hooks
  const topicsLoader = useTopicsLoader({
    api,
    globalProduct: globalProduct || "",
    isKontrolEdge,
  });

  const form = useChangeRequestForm();

  // Load services if KE
  const { items: serviceItems } = useLoadServiceItems(
    api,
    tenantId,
    isKontrolEdge
  );

  // Extract unique customers from serviceItems
  const uniqueCustomers = React.useMemo(() => {
    return extractUniqueCustomers(serviceItems);
  }, [serviceItems]);

  // Filter services based on selected customer (only in partner view)
  const filteredServiceItems = React.useMemo(() => {
    if (!isPartnerView) {
      // Not in partner view: show all services
      return serviceItems;
    }
    // In partner view: filter by selected customer
    return filterServicesByCustomer(serviceItems, form.formState.selectedCustomerId);
  }, [serviceItems, form.formState.selectedCustomerId, isPartnerView]);

  const isLoadingSomething = topicsLoader.loadingTopics || topicsLoader.loadingTwoLevels || isSaving;
  const overlay = useProcessingOverlay(isLoadingSomething);

  const handleCloseModal = () => {
    resetFormState();
    onClose();
  };

  const handleSelectTwoLevel = (twoLabel: string) => {
    topicsLoader.setSelectedTwoLevelLabel(twoLabel);
  };

  const isAttachmentsValid = () => {
    if (isKontrolEdge) return true;
    if (!form.formState.sendResponse) return true;
    const minName = form.formState.contactName.trim().length >= 3;
    const validEmail = isValidEmail(form.formState.contactEmail.trim());
    return minName && validEmail;
  };

  // Tabs via hook
  const {
    activeTab,
    go: goTab,
    canActivate,
  } = useWizardTabs("serviceSelection", {
    serviceSelection: () => true,
    requestInfo: () =>
      isGeneralInfoValidCR(
        isKontrolEdge,
        topicsLoader.selectedTopicLabel,
        topicsLoader.selectedTwoLevelLabel
      ),
    attachments: () =>
      isGeneralInfoValidCR(
        isKontrolEdge,
        topicsLoader.selectedTopicLabel,
        topicsLoader.selectedTwoLevelLabel
      ) &&
      isAdditionalInfoValidShared(isKontrolEdge, {
        cloud,
        region,
        title: form.formState.title,
        description: form.formState.description,
        priority: form.formState.priority,
      }),
  });

  const handleChangeTab = (id: string) => goTab(id);
  
  // Reset form state
  const resetFormState = () => {
    topicsLoader.resetTopics();
    form.resetForm();
    handleChangeTab("serviceSelection");
  };

  const handleCustomerSelect = (customerNameDisplay: string) => {
    form.handleCustomerSelect(customerNameDisplay, uniqueCustomers);
  };

  const handleServiceSelect = (selectedLabels: string[]) => {
    form.handleServiceSelect(selectedLabels, filteredServiceItems);
  };

  const handleSave = async () => {
    if (
      !isAttachmentsValidShared(
        isKontrolEdge,
        form.formState.sendResponse,
        form.formState.contactName,
        form.formState.contactEmail
      )
    )
      return;
    setIsSaving(true);
    try {
      const foundProduct = findProductByLabel(productData, globalProduct || "");
      const productValue = foundProduct ? foundProduct.value : "";
      const foundTopic = findTopicByLabel(topicsLoader.topics, topicsLoader.selectedTopicLabel);
      const topicValue = foundTopic ? foundTopic.value : "";
      const foundTwoLevel = findTwoLevelByLabel(topicsLoader.twoLevelOptions, topicsLoader.selectedTwoLevelLabel);
      const twoLevelValue = foundTwoLevel ? foundTwoLevel.value : "";
      // Priority mapping
      const priorityNumber = mapCRPriority(
        form.formState.priority as PriorityLabel,
        isKontrolEdge
      );
      if (isKontrolEdge) {
        // Validation: require customer only in partner view
        if (isPartnerView && !form.formState.selectedCustomerId) {
          setAlertVariant("error");
          setAlertMessage(
            "Please select a customer and at least one service before creating the ticket."
          );
          setAlertOpen(true);
          setIsSaving(false);
          return;
        }
        if (form.formState.selectedServiceIds.length === 0) {
          setAlertVariant("error");
          setAlertMessage(
            "Please select at least one service before creating the ticket."
          );
          setAlertOpen(true);
          setIsSaving(false);
          return;
        }
        // ========== If product is Kontrol Edge => use createKontrolTicket + minimal fields ==========
        const kontrolPayload: any = {
          requesttype: "CHANGE_REQUEST",
          summary: form.formState.title,
          priority: priorityNumber,
          description: form.formState.description,
          subscriptionID: form.formState.selectedSubscriptionIds.length > 0 
            ? form.formState.selectedSubscriptionIds 
            : [],
          serviceType: form.formState.selectedServiceTypes.length > 0
            ? form.formState.selectedServiceTypes
            : [],
          files: form.formState.attachmentFiles ? Array.from(form.formState.attachmentFiles) : [],
        };
        // Always include end_customer
        // Partner view: use selected customer's ID
        // Customer view: use tenantId (current customer's ID)
        kontrolPayload.end_customer = isPartnerView 
          ? (form.formState.selectedCustomerId as number) 
          : tenantId;
        //console.log("Kontrol ticket created => kontrolPayload", kontrolPayload);
        const response = await createKontrolTicket(api, kontrolPayload);
        if (onTicketCreated) {
        onTicketCreated();
        }
        //console.log("Kontrol ticket created =>", response.data);
      } else {
        // ========== Otherwise => use createTicket + all the normal fields ==========

        const finalPayload: CreateTicketPayload = {
          Tipo: "Change Request",
          product: productValue,
          cloud,
          region,
          TipoSolicitud: topicValue,
          level_3: twoLevelValue,
          Resumen: form.formState.title,
          Prioridad: priorityNumber,
          Descripcion: form.formState.description,
          files: form.formState.attachmentFiles ? Array.from(form.formState.attachmentFiles) : [],
        };
        if (form.formState.sendResponse) {
          finalPayload.Nombre_de_contacto = form.formState.contactName;
          finalPayload.Correo = form.formState.contactEmail;
          finalPayload.phone_number = `${form.formState.selectedCountryCode}${form.formState.contactPhoneNumber.replace(
            /\s+/g,
            ""
          )}`;
        }
        //console.log("Kontrol ticket created => finalPayload", finalPayload);
        await createTicket(api, finalPayload);
      }
      setAlertVariant("success");
      setAlertMessage("¡Ticket created successfully!");
      setAlertOpen(true);
    } catch (err) {
      console.error("Error creating ticket =>", err);
      setAlertVariant("error");
      setAlertMessage(
        "There was a problem creating the ticket, please try again."
      );
      setAlertOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      closeOnBackdropClick={false}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <span className="text-semantic-text-primary">Change Request</span>
          <GenericTooltip
            text="Use this section to tell us about your Change requests."
            position="top"
          />
        </div>
      }
    >
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          {TABS.map((tab) => {
            const disabled = !canActivate(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => handleChangeTab(tab.id)}
                disabled={disabled}
                className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
                    activeTab === tab.id
                      ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary"
                      : disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-500 hover:text-gray-700"
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "serviceSelection" && (
        <ServiceSelectionTab
          isKontrolEdge={isKontrolEdge}
          isPartnerView={isPartnerView}
          topicsLoader={topicsLoader}
          form={form}
          uniqueCustomers={uniqueCustomers}
          filteredServiceItems={filteredServiceItems}
          onCustomerSelect={handleCustomerSelect}
          onServiceSelect={handleServiceSelect}
          onSelectTwoLevel={handleSelectTwoLevel}
          onNext={() => handleChangeTab("requestInfo")}
        />
      )}

      {activeTab === "requestInfo" && (
        <RequestInfoTab
          isKontrolEdge={isKontrolEdge}
          topicsLoader={topicsLoader}
          form={form}
          cloud={cloud}
          region={region}
          onBack={() => handleChangeTab("serviceSelection")}
          onNext={() => handleChangeTab("attachments")}
        />
      )}

      {activeTab === "attachments" && (
        <AttachmentsTab
          isKontrolEdge={isKontrolEdge}
          form={form}
          isSaving={isSaving}
          isAttachmentsValid={isAttachmentsValid}
          onBack={() => handleChangeTab("requestInfo")}
          onSave={handleSave}
        />
      )}

      {overlay}

      <GenericAlert
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => {
          setAlertOpen(false);
          if (alertVariant === "success") {
            handleCloseModal();
          }
        }}
        variant={alertVariant}
        title={alertVariant === "success" ? "Ticket Created" : "Error"}
        description={alertMessage}
        confirmLabel="OK"
      />
    </GenericModal>
  );
};

export default ChangeRequestModal;
